import { useMemo, useState } from "react";
import { CodexTerminalFrame } from "../ui/CodexTerminalFrame";

// ---------------------------------------------------------------------------
// Types matching the Rust drift engine output
// ---------------------------------------------------------------------------

type DriftSeverity = "Info" | "Warning" | "Error";

type DriftRule = {
  id: string;
  name: string;
  pattern: string;
  description: string;
  severity: DriftSeverity;
  file_ext: string[];
  exclude_ext: string[];
  enabled: boolean;
};

type DriftViolation = {
  rule_id: string;
  file_path: string;
  line: number;
  snippet: string;
  message: string;
  severity: DriftSeverity;
};

type DriftPayload = {
  violations: DriftViolation[];
  rules: DriftRule[];
  scanned_at: string;
  project_root: string;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SEVERITY_COLORS: Record<DriftSeverity, string> = {
  Info: "#8be9fd",
  Warning: "#f1fa8c",
  Error: "#ff5555",
};

const SEVERITY_ORDER: Record<DriftSeverity, number> = {
  Error: 0,
  Warning: 1,
  Info: 2,
};

// ---------------------------------------------------------------------------
// Fallback payload for dev/empty state
// ---------------------------------------------------------------------------

const FALLBACK_PAYLOAD: DriftPayload = {
  violations: [],
  rules: [
    { id: "snake-case-functions", name: "snake_case functions", pattern: "", description: "Rust functions should use snake_case", severity: "Warning", file_ext: ["rs"], exclude_ext: [], enabled: true },
    { id: "no-unwrap", name: "no unwrap in production", pattern: "", description: "Avoid .unwrap() in production code", severity: "Warning", file_ext: ["rs"], exclude_ext: [], enabled: true },
    { id: "consistent-imports", name: "consistent imports", pattern: "", description: "Avoid wildcard imports", severity: "Warning", file_ext: ["rs"], exclude_ext: [], enabled: true },
    { id: "no-println-lib", name: "no println in lib", pattern: "", description: "Use tracing instead of println!", severity: "Warning", file_ext: ["rs"], exclude_ext: [], enabled: true },
    { id: "component-naming", name: "component naming", pattern: "", description: "React components should use PascalCase", severity: "Warning", file_ext: ["tsx"], exclude_ext: [], enabled: true },
    { id: "no-any-type", name: "no any type", pattern: "", description: "Avoid :any in TypeScript", severity: "Warning", file_ext: ["ts", "tsx"], exclude_ext: [], enabled: true },
    { id: "max-file-length", name: "max file length", pattern: "500", description: "Files should not exceed 500 lines", severity: "Info", file_ext: [], exclude_ext: [], enabled: true },
  ],
  scanned_at: "",
  project_root: "",
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SeverityBadge({ severity }: { severity: DriftSeverity }) {
  return (
    <span
      className="drift-severity-badge"
      style={{
        color: "#1a1b26",
        background: SEVERITY_COLORS[severity],
        padding: "1px 8px",
        borderRadius: "3px",
        fontSize: "11px",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {severity}
    </span>
  );
}

function SeverityStat({ severity, count }: { severity: DriftSeverity; count: number }) {
  return (
    <div
      className="drift-stat"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
      }}
    >
      <span
        style={{
          width: "10px",
          height: "10px",
          borderRadius: "2px",
          background: SEVERITY_COLORS[severity],
          display: "inline-block",
          flexShrink: 0,
        }}
      />
      <span style={{ color: "#a8b2b8", fontSize: "12px" }}>{severity}</span>
      <strong style={{ color: SEVERITY_COLORS[severity], fontSize: "18px", fontFamily: "var(--font-mono, monospace)" }}>
        {count}
      </strong>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main workbench
// ---------------------------------------------------------------------------

export function DriftWorkbench({ payload }: { payload?: unknown }) {
  const view = (payload as DriftPayload | undefined) ?? FALLBACK_PAYLOAD;
  const [tab, setTab] = useState<"violations" | "rules">("violations");
  const [disabledRules, setDisabledRules] = useState<Set<string>>(new Set());
  const [scanning, setScanning] = useState(false);

  // Compute violation counts by severity.
  const activeViolations = useMemo(
    () => view.violations.filter((v) => !disabledRules.has(v.rule_id)),
    [view.violations, disabledRules],
  );

  const counts = useMemo(() => {
    const c: Record<DriftSeverity, number> = { Info: 0, Warning: 0, Error: 0 };
    for (const v of activeViolations) {
      c[v.severity]++;
    }
    return c;
  }, [activeViolations]);

  const total = counts.Error + counts.Warning + counts.Info;

  // Group violations by file path.
  const groupedByFile = useMemo(() => {
    const map = new Map<string, DriftViolation[]>();
    const sorted = [...activeViolations].sort(
      (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] || a.file_path.localeCompare(b.file_path) || a.line - b.line,
    );
    for (const v of sorted) {
      const arr = map.get(v.file_path);
      if (arr) arr.push(v);
      else map.set(v.file_path, [v]);
    }
    return map;
  }, [activeViolations]);

  const toggleRule = (ruleId: string) => {
    setDisabledRules((prev) => {
      const next = new Set(prev);
      if (next.has(ruleId)) next.delete(ruleId);
      else next.add(ruleId);
      return next;
    });
  };

  const handleScan = () => {
    setScanning(true);
    // In production this would call the backend. For now, signal via IPC.
    if (window.ipc) {
      window.ipc.postMessage(JSON.stringify({ kind: "drift-scan", data: "" }));
    }
    // Auto-reset scanning state after a timeout (backend would push new payload).
    setTimeout(() => setScanning(false), 2000);
  };

  return (
    <main className="ui-shell drift-shell" style={{ background: "#1a1b26", color: "#f8f8f2", minHeight: "100vh" }}>
      <section
        className="drift-workbench"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          padding: "16px 24px",
          gap: "16px",
          fontFamily: "var(--font-mono, 'JetBrains Mono', 'Fira Code', monospace)",
        }}
      >
        {/* -- Header -- */}
        <header
          className="drift-header"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #282a36",
            paddingBottom: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "baseline", gap: "12px" }}>
            <h1 style={{ fontSize: "16px", fontWeight: 700, color: "#f8f8f2", margin: 0 }}>
              Drift Detector
            </h1>
            <span style={{ fontSize: "12px", color: "#6272a4" }}>
              {view.scanned_at ? `scanned ${view.scanned_at}` : "no scan yet"}
            </span>
          </div>
          <button
            type="button"
            onClick={handleScan}
            disabled={scanning}
            style={{
              background: scanning ? "#282a36" : "#50fa7b",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "6px 16px",
              fontSize: "12px",
              fontWeight: 600,
              cursor: scanning ? "wait" : "pointer",
              fontFamily: "inherit",
            }}
          >
            {scanning ? "scanning..." : "scan project"}
          </button>
        </header>

        {/* -- Summary bar -- */}
        <div
          className="drift-summary"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "24px",
            padding: "10px 16px",
            background: "#282a36",
            borderRadius: "6px",
            border: "1px solid #282a36",
          }}
        >
          <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
            <strong style={{ fontSize: "24px", color: "#f8f8f2", fontFamily: "inherit" }}>{total}</strong>
            <span style={{ fontSize: "12px", color: "#6272a4" }}>violations</span>
          </div>
          <div style={{ width: "1px", height: "28px", background: "#282a36" }} />
          <SeverityStat severity="Error" count={counts.Error} />
          <SeverityStat severity="Warning" count={counts.Warning} />
          <SeverityStat severity="Info" count={counts.Info} />
        </div>

        {/* -- Tabs -- */}
        <nav
          className="drift-tabs"
          style={{ display: "flex", gap: "2px", borderBottom: "1px solid #282a36" }}
        >
          {(["violations", "rules"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              style={{
                background: tab === t ? "#282a36" : "transparent",
                color: tab === t ? "#f8f8f2" : "#6272a4",
                border: "none",
                borderBottom: tab === t ? "2px solid #8be9fd" : "2px solid transparent",
                padding: "8px 16px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
                textTransform: "capitalize",
              }}
            >
              {t}
              {t === "violations" ? ` (${total})` : ` (${view.rules.length})`}
            </button>
          ))}
        </nav>

        {/* -- Content -- */}
        <div
          className="drift-content"
          style={{ flex: 1, overflow: "auto", minHeight: 0 }}
        >
          {tab === "violations" ? (
            <ViolationsPanel groupedByFile={groupedByFile} total={total} />
          ) : (
            <RulesPanel rules={view.rules} disabledRules={disabledRules} onToggle={toggleRule} />
          )}
        </div>
      </section>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Violations panel
// ---------------------------------------------------------------------------

function ViolationsPanel({
  groupedByFile,
  total,
}: {
  groupedByFile: Map<string, DriftViolation[]>;
  total: number;
}) {
  if (total === 0) {
    return (
      <CodexTerminalFrame title="drift" prompt="no violations detected" compact>
        <div style={{ padding: "32px", textAlign: "center", color: "#6272a4" }}>
          <p style={{ fontSize: "14px", margin: 0 }}>All clear. Your project follows the configured conventions.</p>
        </div>
      </CodexTerminalFrame>
    );
  }

  return (
    <div className="drift-violations" style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {Array.from(groupedByFile.entries()).map(([filePath, violations]) => (
        <div
          key={filePath}
          className="drift-file-group"
          style={{
            background: "#282a36",
            borderRadius: "6px",
            border: "1px solid #282a36",
            overflow: "hidden",
          }}
        >
          <div
            className="drift-file-header"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 12px",
              background: "#1a1b26",
              borderBottom: "1px solid #282a36",
            }}
          >
            <span style={{ fontSize: "12px", color: "#8be9fd", fontFamily: "inherit" }}>
              {filePath}
            </span>
            <span style={{ fontSize: "11px", color: "#6272a4" }}>
              {violations.length} {violations.length === 1 ? "issue" : "issues"}
            </span>
          </div>

          <div className="drift-file-violations">
            {violations.map((v, idx) => (
              <div
                key={`${v.rule_id}-${v.line}-${idx}`}
                className="drift-violation-row"
                style={{
                  display: "grid",
                  gridTemplateColumns: "52px 80px 1fr",
                  gap: "8px",
                  alignItems: "start",
                  padding: "8px 12px",
                  borderBottom: idx < violations.length - 1 ? "1px solid #282a36" : "none",
                  fontSize: "12px",
                }}
              >
                <span style={{ color: "#6272a4", fontFamily: "inherit" }}>
                  L{v.line}
                </span>
                <SeverityBadge severity={v.severity} />
                <div style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
                  <span style={{ color: "#f8f8f2", fontWeight: 600, fontSize: "12px" }}>
                    {v.message}
                  </span>
                  <code
                    style={{
                      color: "#a8b2b8",
                      fontSize: "11px",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                      background: "#1a1b26",
                      padding: "2px 6px",
                      borderRadius: "3px",
                      display: "inline-block",
                      maxWidth: "100%",
                    }}
                  >
                    {v.snippet}
                  </code>
                  <span style={{ color: "#6272a4", fontSize: "11px" }}>
                    rule: {v.rule_id}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rules panel
// ---------------------------------------------------------------------------

function RulesPanel({
  rules,
  disabledRules,
  onToggle,
}: {
  rules: DriftRule[];
  disabledRules: Set<string>;
  onToggle: (id: string) => void;
}) {
  return (
    <div className="drift-rules" style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      {rules.map((rule) => {
        const enabled = !disabledRules.has(rule.id);
        return (
          <div
            key={rule.id}
            className="drift-rule-row"
            style={{
              display: "grid",
              gridTemplateColumns: "40px 1fr 80px 56px",
              gap: "8px",
              alignItems: "center",
              padding: "10px 12px",
              background: "#282a36",
              borderRadius: "6px",
              border: "1px solid #282a36",
              opacity: enabled ? 1 : 0.45,
            }}
          >
            <button
              type="button"
              onClick={() => onToggle(rule.id)}
              style={{
                width: "32px",
                height: "18px",
                borderRadius: "9px",
                border: "none",
                background: enabled ? "#50fa7b" : "rgba(255,255,255,0.06)",
                position: "relative",
                cursor: "pointer",
                transition: "background 0.15s",
                padding: 0,
              }}
              aria-label={`Toggle rule ${rule.name}`}
            >
              <span
                style={{
                  position: "absolute",
                  top: "2px",
                  left: enabled ? "16px" : "2px",
                  width: "14px",
                  height: "14px",
                  borderRadius: "7px",
                  background: "#fff",
                  transition: "left 0.15s",
                }}
              />
            </button>

            <div style={{ display: "flex", flexDirection: "column", gap: "1px", minWidth: 0 }}>
              <strong style={{ fontSize: "13px", color: "#f8f8f2" }}>{rule.name}</strong>
              <span style={{ fontSize: "11px", color: "#a8b2b8" }}>{rule.description}</span>
              {rule.file_ext.length > 0 && (
                <span style={{ fontSize: "10px", color: "#6272a4" }}>
                  applies to: {rule.file_ext.map((e) => `.${e}`).join(", ")}
                </span>
              )}
            </div>

            <SeverityBadge severity={rule.severity} />

            <span style={{ fontSize: "11px", color: "#6272a4", fontFamily: "inherit", textAlign: "right" }}>
              {rule.id}
            </span>
          </div>
        );
      })}
    </div>
  );
}
